import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AiProfileInfo } from '@/customTypes/chapter';
import {
  StoryGameArrowRight,
  StoryGameArrowLeft,
  ProfileEditIcon,
} from 'public/assets';
import { useHoverTouch } from '@/hooks/useHoverTouch';
import StoryGameMyImageDefaultBubble from '@/components/commons/popup/StoryGameMyImageDefaultBubble';
import useTranslation from 'next-translate/useTranslation';

interface StoryGameCharacterNameInputProps {
  onPrevious?: () => void;
  onNext?: () => void;
  firstName: string;
  onChangeAiProfile: (profile: AiProfileInfo) => void;
  lastName: string;
  characterImage?: string | File;
  onProfileEdit: () => void;
  placeholderLastName: string;
  placeholderFirstName: string;
  isDefault: boolean;
  displayName: string;
  isError?: boolean;
}

const StoryGameCharacterNameInput: React.FC<
  StoryGameCharacterNameInputProps
> = ({
  onPrevious,
  onNext,
  firstName,
  onChangeAiProfile,
  lastName,
  characterImage,
  onProfileEdit,
  placeholderLastName,
  placeholderFirstName,
  isDefault,
  displayName,
  isError,
}) => {
  const { t } = useTranslation();
  const [name1, setName1] = useState(lastName);
  const [name2, setName2] = useState(firstName);
  const [preview, setPreview] = useState<string>();
  const { isHovered, hoverProps } = useHoverTouch();

  useEffect(() => {
    setName1(lastName);
    setName2(firstName);
  }, [firstName, lastName]);

  useEffect(() => {
    // characterImage가 File타입일 경우 실행

    if (characterImage instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(characterImage);
    } else {
      setPreview(undefined);
    }
  }, [characterImage]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
    key: 'firstName' | 'lastName',
  ) => {
    const value = e.target.value;
    onChange(value);
    // firstName, lastName의 value가 둘 다 있을 떄 isError = false
    const isErrorValue =
      isError && (key === 'lastName' ? !value || !name2 : !name1 || !value);
    onChangeAiProfile({ [key]: value, isError: isErrorValue });
    // isError && e.target.classList.toggle('empty', value === '');
  };

  return (
    <div css={styles.container}>
      <span
        css={styles.infoIcon}
        onMouseEnter={hoverProps.onMouseEnter}
        onMouseLeave={hoverProps.onMouseLeave}
        onTouchStart={hoverProps.onTouchStart}
        onTouchEnd={hoverProps.onTouchEnd}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <rect
            x="0.75"
            y="0.75"
            width="18.5"
            height="18.5"
            rx="9.25"
            stroke="white"
            strokeOpacity="0.75"
            strokeWidth="1.5"
          />
          <rect
            x="9"
            y="7.5"
            width="2"
            height="8"
            rx="1"
            fill="white"
            fillOpacity="0.75"
          />
          <rect
            x="9"
            y="4.5"
            width="2"
            height="2"
            rx="1"
            fill="white"
            fillOpacity="0.75"
          />
        </svg>
      </span>
      <div
        style={{
          position: 'absolute',
          top: -92,
          right: -7,
        }}
      >
        {isHovered && (
          <StoryGameMyImageDefaultBubble
            trianglePosition="bottom"
            textAlign="left"
          >
            {t('storygame_ai_onboarding_screen_description_report_nasty')}
          </StoryGameMyImageDefaultBubble>
        )}
      </div>
      <button css={styles.navButton} onClick={onPrevious}>
        <StoryGameArrowLeft color="white" />
      </button>
      <div css={styles.innerContainer}>
        {preview || characterImage ? (
          <>
            <div css={styles.imageContainer}>
              {!isDefault && (
                <div css={styles.profileEditIcn} onClick={onProfileEdit}>
                  <ProfileEditIcon />
                </div>
              )}
            </div>
            <img
              src={preview ?? (characterImage as string)}
              alt="Character"
              css={styles.image}
            />
          </>
        ) : (
          <div css={styles.emptyImageContainer}>
            <div css={styles.emptyImage}></div>
            {!isDefault && (
              <div css={styles.profileEditIcn} onClick={onProfileEdit}>
                <ProfileEditIcon />
              </div>
            )}
          </div>
        )}
        <div css={styles.contentContainer}>
          <h2 css={styles.title}>
            {t('storygame_ai_onboarding_popup_title_what_name', {
              value: displayName,
            })}
          </h2>
          <div css={styles.inputContainer}>
            <input
              type="text"
              value={name1}
              onChange={(e) => handleInputChange(e, setName1, 'lastName')}
              placeholder={placeholderLastName}
              css={styles.input}
              className={isError && name1 === '' ? 'empty' : ''}
            />
            <input
              type="text"
              value={name2}
              onChange={(e) => handleInputChange(e, setName2, 'firstName')}
              placeholder={placeholderFirstName}
              css={styles.input}
              className={isError && name2 === '' ? 'empty' : ''}
            />
          </div>
        </div>
      </div>
      <button css={styles.navButton} onClick={onNext}>
        <StoryGameArrowRight color={'white'} />
      </button>
    </div>
  );
};

const styles = {
  container: css`
    position: relative;
    width: 32vw;
    min-width: 512px;
    border-top: 2px solid;
    border-bottom: 2px solid;
    border-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    border-image-slice: 1;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.75) 20%,
      rgba(0, 0, 0, 0.75) 80%,
      rgba(0, 0, 0, 0) 100%
    );

    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 40px;
  `,
  innerContainer: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
  `,
  imageContainer: css`
    position: absolute;
    top: -55px;
    width: 80px;
    height: 80px;
    border-radius: 40px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-radius: 128px;
    box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.5) inset;
    background: transparent;
    z-index: 1;
  `,
  image: css`
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 40px;
    position: absolute;
    background: white;
    top: -55px;
  `,
  contentContainer: css`
    width: 100%;
    padding: 0px 20px;
  `,
  title: css`
    color: white;
    font-size: 18px;
    text-align: center;
    margin-bottom: 10px;
  `,
  inputContainer: css`
    display: flex;
    justify-content: space-between;
    gap: 10px;
  `,
  input: css`
    flex: 1;
    padding: 10px;
    border: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
    background-color: transparent;
    color: white;
    font-size: 16px;
    text-align: center;
    transition: border-bottom-color 0.3s ease;
    width: 128px;
    &::placeholder {
      color: #ffffff80;
    }

    &:focus {
      outline: none;
      border-bottom-color: var(--purpleHighlight, #9457eb);
    }

    &.empty:not(:focus) {
      border-bottom-color: var(--red, #ff3a58);
    }
  `,
  navButton: css`
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 10px;
    z-index: 1;
    &:hover {
      opacity: 0.8;
    }
  `,
  profileEditIcn: css`
    position: absolute;
    display: flex;
    width: 28px;
    height: 28px;
    justify-content: center;
    align-items: center;
    bottom: 0px;
    right: 0px;
    border-radius: 100px;
    border: 1px solid var(--blackA25, rgba(0, 0, 0, 0.25));
    background: #fff;
    cursor: pointer;
  `,
  emptyImageContainer: css`
    position: absolute;
    top: -55px;
    width: 80px;
    height: 80px;
    border: 2px solid var(--whiteA50, rgba(255, 255, 255, 0.5));
    border-radius: 40px;
    background: #ddd;
    box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.5) inset;
    border-radius: 40px;
  `,
  emptyImage: css`
    width: 100%;
    height: 100%;
  `,
  infoIcon: css`
    position: absolute;
    top: -40px;
    right: 0;
    color: #333;
    border: none;
    padding: 5px 10px;
    border-radius: 5px;
    cursor: pointer;
    z-index: 10;
  `,
};

export default StoryGameCharacterNameInput;
